import React from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { company, CONTACT_EMAIL } from '../../data/site';
import { useAdminData } from '../../contexts/AdminDataContext';
import { formatDate } from '../../utils/format';
import { calculateTotals, formatKr, lineTotal } from '../../utils/quote';
import type { QuoteLine } from '../../types/quote';
const ease = [0.23, 1, 0.32, 1] as const;
interface Props {
  quoteNumber: string;
  title: string;
  description: string;
  lines: QuoteLine[];
  quoteDate: string;
  validUntil: string;
  customerNotes: string;
  customer: {
    name: string;
    address: string;
    email: string;
    phone: string;
  };
  onClose: () => void;
}

/** Kundens visning af tilbuddet — samme indhold som en senere mailudsendelse. */
export function QuotePreview({
  quoteNumber,
  title,
  description,
  lines,
  quoteDate,
  validUntil,
  customerNotes,
  customer,
  onClose
}: Props) {
  const {
    settings
  } = useAdminData();
  const totals = calculateTotals(lines);
  return <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 px-4 py-8" role="dialog" aria-modal="true" aria-labelledby="forhaandsvis-titel" data-id="element-442" data-mp="2u9sbnqbcm3tb">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.24,
      ease
    }} className="lv-light mx-auto w-full max-w-3xl rounded-card bg-white p-6 text-ink md:p-10" data-id="element-443" data-mp="2kkh2y4sdj15y">
        <div className="flex items-start justify-between gap-4" data-id="element-444" data-mp="2akhxigr93qxb">
          <img src={company.logoWide} alt={settings.companyName} className="h-10 w-auto" data-id="element-445" data-mp="2fle9zipo4lec" />
          <button type="button" onClick={onClose} aria-label="Luk forhåndsvisning" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-line text-ink-soft transition-colors duration-200 ease-smooth hover:border-ink/30 hover:text-ink" data-id="element-446" data-mp="21lwfcmpoydx2">
            <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-447" data-mp="4rryufzgt6ap" />
          </button>
        </div>

        <div className="mt-8 flex flex-wrap items-start justify-between gap-6" data-id="element-448" data-mp="2fhb5jho809f">
          <div data-id="element-449" data-mp="3uhp89ko5z1yk">
            <h2 id="forhaandsvis-titel" className="font-display text-2xl font-bold tracking-tight text-ink md:text-3xl" data-id="element-450" data-mp="3uh5py2ulqbk0">
              Tilbud
            </h2>
            <p className="mt-1 text-[15px] text-ink-soft" data-id="element-451" data-mp="3kl2rb6yykv1t">{quoteNumber}</p>
          </div>
          <dl className="text-[15px]" data-id="element-452" data-mp="m2ovrz1eq1vy">
            <div className="flex justify-between gap-8" data-id="element-453" data-mp="m1m6fvjqg7i5">
              <dt className="text-ink-muted" data-id="element-454" data-mp="h32ijplkwln5">Dato</dt>
              <dd className="text-ink" data-id="element-455" data-mp="h5omlf99gj0m">{formatDate(quoteDate)}</dd>
            </div>
            <div className="mt-1 flex justify-between gap-8" data-id="element-456" data-mp="carcs645q5mo">
              <dt className="text-ink-muted" data-id="element-457" data-mp="7chkq4dqbl5a">Gyldig til</dt>
              <dd className="text-ink" data-id="element-458" data-mp="14swpxrn4pi00">{formatDate(validUntil)}</dd>
            </div>
          </dl>
        </div>

        <div className="mt-8 grid gap-6 border-y border-line py-6 sm:grid-cols-2" data-id="element-459" data-mp="102gcw8n7zye6">
          <div data-id="element-460" data-mp="17dcmluo69tpy">
            <p className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-461" data-mp="17dur1ekzne8y">Til</p>
            <p className="mt-2 text-[15px] font-medium text-ink" data-id="element-462" data-mp="1ccfstifw25zi">{customer.name || '—'}</p>
            <p className="text-[15px] text-ink-soft" data-id="element-463" data-mp="1ce8zuyueys81">{customer.address}</p>
            <p className="text-[15px] text-ink-soft" data-id="element-464" data-mp="1ha7is6wz3i12">{customer.phone}</p>
            <p className="break-all text-[15px] text-ink-soft" data-id="element-465" data-mp="1mbn31wrbtkie">{customer.email}</p>
          </div>
          <div className="sm:text-right" data-id="element-466" data-mp="1qb5r31eg2bpg">
            <p className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-467" data-mp="1qa4akhensx0c">Fra</p>
            <p className="mt-2 text-[15px] font-medium text-ink" data-id="element-468" data-mp="1v9gzr8t06g63">{settings.companyName}</p>
            <p className="text-[15px] text-ink-soft" data-id="element-469" data-mp="202iixt08t1vh">CVR: {settings.cvr}</p>
            <p className="text-[15px] text-ink-soft" data-id="element-470" data-mp="2501uum9gx2k9">{settings.phone}</p>
            <p className="break-all text-[15px] text-ink-soft" data-id="element-471" data-mp="2a0g6eeaa2un1">{CONTACT_EMAIL}</p>
          </div>
        </div>

        <h3 className="mt-8 font-display text-xl font-bold tracking-tight text-ink" data-id="element-472" data-mp="2e19tj5fwc5ev">{title || 'Opgave'}</h3>
        {description && <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-ink-soft" data-id="element-473" data-mp="3bp38zihc3d2w">{description}</p>}

        <table className="mt-6 w-full text-left text-[15px]" data-id="element-474" data-mp="36pgy3foza8x9">
          <thead data-id="element-475" data-mp="3khwsjs3tcymk">
            <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-476" data-mp="3kigd7gkum14a">
              <th scope="col" className="py-2 font-medium" data-id="element-477" data-mp="3ghwei8kmmpg1">Beskrivelse</th>
              <th scope="col" className="py-2 font-medium" data-id="element-478" data-mp="2sstnwb80lm9e">Antal</th>
              <th scope="col" className="py-2 font-medium" data-id="element-479" data-mp="2nsyvg38lcom8">Enhedspris</th>
              <th scope="col" className="py-2 text-right font-medium" data-id="element-480" data-mp="2nus23ij7f8u8">I alt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line" data-id="element-481" data-mp="2wtdj39912fsw">
            {lines.map(line => <tr key={line.id} data-id="element-482" data-mp="2wvqccik8kcl9">
                <td className="py-2.5 text-ink" data-id="element-483" data-mp="h1py8c7agdqg">{line.description || '—'}</td>
                <td className="whitespace-nowrap py-2.5 text-ink-soft" data-id="element-484" data-mp="c36cocwlx3ym">
                  {line.quantity} {line.unit}
                </td>
                <td className="whitespace-nowrap py-2.5 text-ink-soft" data-id="element-485" data-mp="kj3w6845gio0">{formatKr(line.unitPrice)}</td>
                <td className="whitespace-nowrap py-2.5 text-right font-medium text-ink" data-id="element-486" data-mp="omtnh5c7lecq">
                  {formatKr(lineTotal(line))}
                </td>
              </tr>)}
            {lines.length === 0 && <tr data-id="element-487" data-mp="flc4nf7f2ykc">
                <td colSpan={4} className="py-6 text-center text-sm text-ink-muted" data-id="element-488" data-mp="fmwzz71k2lj9">
                  Ingen linjer på tilbuddet endnu.
                </td>
              </tr>}
          </tbody>
        </table>

        <dl className="mt-6 ml-auto max-w-xs divide-y divide-line/70 text-[15px]" data-id="element-489" data-mp="tld8n3izam41">
          <div className="flex justify-between gap-6 py-2" data-id="element-490" data-mp="yjy8v2y8d5sh">
            <dt className="text-ink-soft" data-id="element-491" data-mp="yidbl5kj78qx">Subtotal ekskl. moms</dt>
            <dd className="font-medium text-ink" data-id="element-492" data-mp="1m98zvsps3ltf">{formatKr(totals.subtotal)}</dd>
          </div>
          <div className="flex justify-between gap-6 py-2" data-id="element-493" data-mp="1r0ipitbbup5b">
            <dt className="text-ink-soft" data-id="element-494" data-mp="1r228wssa5gbb">Moms</dt>
            <dd className="font-medium text-ink" data-id="element-495" data-mp="20v6fvi3pjrpw">{formatKr(totals.vatAmount)}</dd>
          </div>
          <div className="flex justify-between gap-6 py-2" data-id="element-496" data-mp="1vy6azailgv0d">
            <dt className="font-medium text-ink" data-id="element-497" data-mp="1qy1pf62txa3a">Total inkl. moms</dt>
            <dd className="font-display text-lg font-bold text-ink" data-id="element-498" data-mp="1qw8l3xfov1yg">{formatKr(totals.total)}</dd>
          </div>
        </dl>

        {customerNotes && <div className="mt-8 rounded-xl bg-canvas p-5" data-id="element-499" data-mp="1hxn1s02e7iwm">
            <p className="text-xs uppercase tracking-wider text-ink-muted" data-id="element-500" data-mp="1hz6ly1r66e3o">Vilkår og bemærkninger</p>
            <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-ink-soft" data-id="element-501" data-mp="1d4txteer0ell">
              {customerNotes}
            </p>
          </div>}
      </motion.div>
    </div>;
}